/// src/routes/guards.js
/* eslint-disable */
import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

/* -------------------- tiny auth hook -------------------- */
function useAuthState() {
  const [state, setState] = React.useState({ ready: false, user: null });
  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) =>
      setState({ ready: true, user: u || null })
    );
    return () => unsub();
  }, []);
  return state;
}

/* ---------------------- user doc fetch ------------------- */
async function fetchUserDoc(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.warn("[guards] fetchUserDoc failed:", e?.message || e);
    return null;
  }
}

/* --------------- unified resolver (no side effects) --------------- */
function useRoleSchoolAndFlags() {
  const { ready, user } = useAuthState();
  const [loaded, setLoaded] = React.useState(false);
  const [role, setRole] = React.useState(null);
  const [schoolId, setSchoolId] = React.useState(
    () => localStorage.getItem("schoolId") || null
  );

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!ready) return;

      if (!user) {
        if (!cancelled) {
          setRole(null);
          setSchoolId(null);
          setLoaded(true);
        }
        return;
      }

      // Try local cache first to avoid flicker
      const ls = localStorage.getItem("schoolId") || null;

      const udoc = await fetchUserDoc(user.uid);
      const r = (udoc?.role ? String(udoc.role) : "").toLowerCase() || null;

      // Accept BOTH orgId and schoolId (backend sometimes writes orgId)
      const resolvedSchool =
        ls || udoc?.orgId || udoc?.schoolId || null;

      if (!cancelled) {
        setRole(r);
        setSchoolId(resolvedSchool);
        setLoaded(true);
      }

      // Write-through cache (non-blocking)
      if (!ls && resolvedSchool) {
        try { localStorage.setItem("schoolId", resolvedSchool); } catch {}
      }
    })();

    return () => { cancelled = true; };
  }, [ready, user]);

  const isAdmin = role === "admin";
  const isSchoolRole = role === "school_admin" || role === "teacher" || role === "student";
  const hasSchool = !!schoolId;

  return { ready, loaded, user, role, isAdmin, isSchoolRole, hasSchool, schoolId };
}

/* ------------------------ Guards ------------------------ */

/** Only global admins may pass */
export function RequireAdmin() {
  const { ready, loaded, user, isAdmin } = useRoleSchoolAndFlags();
  if (!ready || !loaded) return null; // avoid flicker/early redirects
  if (!user || !isAdmin) return <Navigate to="/admin-login" replace />;
  return <Outlet />;
}

/** If an admin hits user routes, push them to the admin dashboard */
export function ForbidAdminOnUserRoutes() {
  const { ready, loaded, user, isAdmin } = useRoleSchoolAndFlags();
  if (!ready || !loaded) return null;
  if (user && isAdmin) return <Navigate to="/admin-dashboard" replace />;
  return <Outlet />;
}

/** Signed-in users only; keep admins out of consumer area */
export function RequireAuth() {
  const { ready, loaded, user, isAdmin } = useRoleSchoolAndFlags();
  if (!ready || !loaded) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (isAdmin) return <Navigate to="/admin-dashboard" replace />;
  return <Outlet />;
}

/**
 * School-required routes
 *
 * Rules:
 * 1) While loading → render nothing (no premature redirect).
 * 2) If not signed in → to /login.
 * 3) Allow /school-dashboard to mount even if schoolId not resolved yet (so it can establish context).
 * 4) If user has a school role but no schoolId resolved → send to /school-dashboard to establish/choose org.
 * 5) If user is NOT a school role → send to /free-dashboard.
 * 6) Otherwise (has school) → allow.
 */
export function RequireSchoolContext() {
  const { ready, loaded, user, isSchoolRole, hasSchool } = useRoleSchoolAndFlags();
  const location = useLocation();

  if (!ready || !loaded) return null; // important: no early redirect
  if (!user) return <Navigate to="/login" replace />;

  // Always allow the dashboard route to resolve/establish school context
  if (location.pathname === "/school-dashboard") {
    return <Outlet />;
  }

  // If user isn't a school user at all, keep them in consumer land
  if (!isSchoolRole) {
    return <Navigate to="/free-dashboard" replace />;
  }

  // School user but context not resolved yet → go to the dashboard to set/pick org
  if (!hasSchool) {
    return <Navigate to="/school-dashboard" replace />;
  }

  return <Outlet />;
}
